import { TranslatePipe } from '@ngx-translate/core';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Transition } from '@uirouter/angular';
import * as moment from 'moment';

import {
    Contact,
    Company,
    CompanyService,
    CompanyGroupService,
    GraphQlService,
} from '../../../../_services';
import { CompanyGroup } from '../../../../_services/company/companyGroup/companyGroup.model';
import { SessionService } from '../../../../_services/session/session.service';
import { ToasterService } from '../../../_shared/toaster/toaster.service';
import { ContactService } from '../../../../_services/contact/contact.service';
import { TrainingSessionCompanyHistory } from '../../../../_services/session/company/history/training-session-history.model';
import { TrainingSessionCompanyHistoryService } from '../../../../_services/session/company/history/training-session-history.service';
import { DownloadWithToastsService } from '../../../_shared/download-with-toasts';

@Component({
    selector: 'company-detail',
    templateUrl: './detail.component.html',
    styleUrls: ['./detail.component.scss'],
    providers: [TranslatePipe],
})
export class CompanyDetailComponent implements OnInit {
    @Input() company: Company;
    @Input() type: string;
    @Output() getCompany: EventEmitter<any> = new EventEmitter<Company>();

    companyGroups: CompanyGroup[];
    contacts: Contact[];
    companyHistories: TrainingSessionCompanyHistory[];
    companyUpcoming: TrainingSessionCompanyHistory[];
    subCompanies: Company[];
    companyId: string;
    companyName: string;
    usersNames: string[] = [];
    users: any;
    evaluationExportRequest = {
        minStartDate: '',
        maxStartDate: moment().format('YYYY-MM-DD'),
        exportFormat: 'CSV',
    };

    companyGroupName: string = '';

    private companyGroupSelected: boolean;

    constructor(
        private contactService: ContactService,
        private companyService: CompanyService,
        private companyHistoryService: TrainingSessionCompanyHistoryService,
        private companyGroupService: CompanyGroupService,
        private sessionService: SessionService,
        private trans: Transition,
        private toasterService: ToasterService,
        private downloadService: DownloadWithToastsService,
        private graphQlService: GraphQlService
    ) {}

    ngOnInit(): void {
        this.fetchCommercials();
        this.initCompanyGroupField();

        if (this.type !== 'create' && this.type !== 'createInModal') {
            this.type = 'update';
        }
        if (this.type === 'update') {
            this.companyId = this.trans.params()['id'];
            this.fetchCompany(this.companyId);
            this.getAllContactsByCompanyId(this.companyId);
            this.getCompanyHistory(this.companyId);
            this.getCompanyUpcoming(this.companyId);
        } else {
            this.companyName = null;
        }

        this.evaluationExportRequest.minStartDate = moment()
            .subtract(1, 'year')
            .startOf('year')
            .format('YYYY-MM-DD');
        this.evaluationExportRequest.maxStartDate = moment()
            .subtract(1, 'year')
            .endOf('year')
            .format('YYYY-MM-DD');
    }

    searchCommercials(keyword: string) {
        return this.sessionService.searchBackofficeUsers(keyword);
    }

    private fetchCommercials(): void {
        this.sessionService.getBackofficeUsers().subscribe((users) => {
            this.users = users;
            users.map((user) =>
                this.usersNames.push(user.firstname + ' ' + user.lastname)
            );
        });
    }

    formatCommercials(data: any): string {
        return data.fullname
            ? `<span><strong>${data.fullname}</strong></span>`
            : '';
    }

    fetchCompany(id) {
        this.companyService.findById(id).subscribe((company) => {
            if (!company['companyExtended']) {
                company['companyExtended'] = { commercial: null };
            }
            this.company = company;
            this.companyName = company.name;
            this.subCompanies = company.subCompanies;
            this.setCompanyGroup();
        });
    }

    async getAllContactsByCompanyId(id) {
        this.contacts = await this.contactService
            .findContactsByCompanyId(id)
            .toPromise();
    }

    getCompanyHistory(id) {
        this.graphQlService
            .post({
                query: `
                query fetchCompanyTrainingSessionHistory($companyId: bigint!) {
                    trainingSessions: training_session(
                        order_by: { start_date: desc }
                        where: {
                            _and: [
                                { deleted: { _eq: false } }
                                {
                                    student_participations: {
                                        registration: { deleted: { _eq: false } }
                                    }
                                }
                                {
                                    student_participations: {
                                        registration_detail: { deleted: { _eq: false } }
                                    }
                                }
                                {
                                    _or: [
                                        {
                                            student_participations: {
                                                registration: {
                                                    company_id: { _eq: $companyId }
                                                }
                                            }
                                        }
                                        {
                                            student_participations: {
                                                student: { company_id: { _eq: $companyId } }
                                            }
                                        }
                                    ]
                                }
                            ]
                        }
                    ) {
                        id
                        training: training_session_training {
                            title
                        }
                        startDate: start_date
                        studentParticipations: student_participations(
                            where: {
                                _and: [
                                    { registration: { deleted: { _eq: false } } }
                                    { registration_detail: { deleted: { _eq: false } } }
                                    {
                                        _or: [
                                            {
                                                registration: {
                                                    company_id: { _eq: $companyId }
                                                }
                                            }
                                            { student: { company_id: { _eq: $companyId } } }
                                        ]
                                    }
                                ]
                            }
                        ) {
                            registration {
                                company {
                                    id
                                    name
                                }
                            }
                            student {
                                id
                                firstName: first_name
                                lastName: last_name
                                company {
                                    id
                                    name
                                }
                            }
                            studentEvaluation: student_evaluation {
                                overallEvaluation: overall_evaluation
                            }
                        }
                    }
                }
            `,
                variables: {
                    companyId: id,
                },
                role: 'world',
            })
            .map((response) => {
                return response.trainingSessions.map((session) => {
                    return {
                        sessionId: session.id,
                        startDate: session.startDate,
                        trainingTitle: session.training.title,
                        students: session.studentParticipations.map(
                            (participation) => ({
                                averageEvaluation:
                                    participation.studentEvaluation &&
                                    participation.studentEvaluation
                                        .overallEvaluation,
                                employerCompany: participation.student.company,
                                firstname: participation.student.firstName,
                                lastname: participation.student.lastName,
                                registeringCompany:
                                    participation.registration.company,
                            })
                        ),
                    };
                });
            })
            .subscribe((sessions) => {
                this.companyHistories = sessions;
            });
    }

    getCompanyUpcoming(id) {
        this.companyHistoryService
            .getCompanyUpcoming(id)
            .subscribe((companyUpcoming) => {
                this.companyUpcoming = companyUpcoming;
            });
    }

    save(): void {
        if (this.company.companyGroup) {
            this.company.companyGroup = JSON.parse(
                JSON.stringify(this.company.companyGroup)
            );
        }

        const company = JSON.parse(JSON.stringify(this.company));

        if (!company['companyExtended']) {
            company['companyExtended'] = { commercial: null };
        }

        if (!this.companyGroupName) {
            company.companyGroup = null;
        }
        company['companyExtended'].companyGroup = company.companyGroup;

        if (this.type === 'createInModal') {
            this.companyService.create(company).subscribe(
                (response) => {
                    if (response !== null) {
                        this.getCompany.emit(response);
                    }
                },
                (error) => {
                    this.toasterService.error();
                }
            );
        } else if (this.type !== 'create') {
            this.companyService.update(company).subscribe(
                (response) => {
                    if (response !== null) {
                        this.getCompany.emit(response);
                        this.isSuccessful();
                    }
                },
                (error) => {
                    this.toasterService.error();
                }
            );
        } else {
            this.companyService.create(company).subscribe(
                (response) => {
                    if (response !== null) {
                        this.isSuccessful(response.id);
                    }
                },
                (error) => {
                    this.toasterService.error();
                }
            );
        }
    }

    removeCompany(company: Company) {
        company.deleted = true;
        this.companyService.update(company).subscribe();
    }

    private isSuccessful(id?: number) {
        this.toasterService.success();
        window.setTimeout(() => {
            window.scrollTo(0, document.body.scrollHeight);
        }, 0);
        window.setTimeout(() => {
            if (typeof id !== 'undefined') {
                window.location.href = '#/companies/' + id;
            } else {
                window.location.href = '#/companies';
            }
        }, 1542);
    }

    focusOnCompanyGroupAutocomplete() {
        this.companyGroupSelected = false;
        this.company.companyGroup.name = '';
    }

    formatCompanyGroups(data: any): string {
        return data.name ? `<span><strong>${data.name}</strong></span>` : '';
    }

    changeCompanyGroup(newVal) {
        this.companyGroupSelected = true;
        if (
            newVal !== null &&
            typeof newVal !== 'undefined' &&
            newVal.id !== null &&
            typeof newVal.id !== 'undefined'
        ) {
            this.company.companyGroup = newVal;
        }
    }

    searchCompanyGroups(keyword: string) {
        return this.companyGroupService.search(keyword);
    }

    private setCompanyGroup() {
        if (this.company.companyGroup == null) {
            this.company.companyGroup = null;
        } else {
            this.companyGroupName = this.company.companyGroup.name;
        }
    }

    removeFromCompanyGroup() {
        this.companyGroupName = '';
        this.company.companyGroup = null;
    }

    removeFromCompanyCommercial() {
        if (this.company['companyExtended']) {
            this.company['companyExtended'].commercial = null;
        }
    }

    private initCompanyGroupField() {
        this.companyGroupSelected = true;
    }

    isCompanyGroupValid() {
        if (this.companyGroupName == '' || !this.company.companyGroup) {
            return true;
        }

        if (!this.company.companyGroup) {
            return false;
        }

        return this.companyGroupName == this.company.companyGroup.name;
    }

    isCompanyCommercialValid() {
        return true;
    }
    async extractEvaluations() {
        await this.downloadService.downloadAndOpenDocument(
            `/api/companies/${this.companyId}/evaluations` +
                `?minStartDate=${this.evaluationExportRequest.minStartDate}` +
                `&maxStartDate=${this.evaluationExportRequest.maxStartDate}` +
                `&exportFormat=${this.evaluationExportRequest.exportFormat}`,
            this.evaluationExportRequest.exportFormat === 'PDF'
                ? 'application/pdf'
                : 'text/csv'
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/directory/company/detail/detail.component.ts